/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { KioskContext } from "components/ui/Kiosk";
import aeonik from "components/utils/aeonik";
import type Highcharts from "highcharts";
import PayRatioHistoryQuery from "lib/graphql/queries/PayRatioHistoryQuery";
import type {
  IPayRatioHistoryQuery,
  IPayRatioHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/PayRatioHistoryQuery.generated";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import { ISecurityCountry } from "types/graphqlTypes";

export default function PayRatioHistoryChart() {
  const intl = useIntl();
  const issuer = useRootIssuer();
  const tickerSymbol = issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "";
  const { data, loading } = useQuery<IPayRatioHistoryQuery, IPayRatioHistoryQueryVariables>(PayRatioHistoryQuery, {
    skip: !tickerSymbol,
    variables: {
      country: ISecurityCountry.Us,
      tickerSymbol: issuer.__typename === "AmericanCompanyIssuer" ? issuer.primaryTickerSymbol : "",
    },
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (
    !tickerSymbol ||
    !data ||
    !data.issuerByTickerSymbol ||
    data.issuerByTickerSymbol.__typename !== "AmericanCompanyIssuer" ||
    data.issuerByTickerSymbol.payRatioHistory.length === 0
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const payRatioHistory = [...(data?.issuerByTickerSymbol.payRatioHistory || [])].reverse().slice(0, 7);
  const labels = {
    ratio: intl.formatMessage({
      defaultMessage: "Relação",
      id: "eJquNI",
    }),
    medianEmployeePay: intl.formatMessage({
      defaultMessage: "Salário Médio",
      id: "3JYQSl",
    }),
    ceoPay: intl.formatMessage({
      defaultMessage: "Salário do CEO",
      id: "rfe0d4",
    }),
  };

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
      height: null,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      enabled: false,
      padding: 12,
    },
    xAxis: {
      gridLineWidth: 0,
      gridLineColor: colors.slate[100],
      lineWidth: 0,
      lineColor: colors.slate[100],
      tickWidth: 0,
      labels: {
        enabled: true,
        format: "{value}",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickInterval: 1,
      crosshair: {
        color: colors.slate[300],
      },
    },
    yAxis: {
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 0,
      lineColor: colors.slate[100],
      tickWidth: 0,
      tickPosition: "outside",
      tickColor: colors.slate[100],
      endOnTick: false,
      labels: {
        align: "center",
        enabled: true,
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series: [
      {
        type: "bar",
        data: payRatioHistory.map((node) => {
          return {
            x: node.fiscalYear,
            y: node.payRatio,
            custom: node,
          };
        }),
        name: "For",
        color: colors.violet[100],
        borderColor: colors.violet[600],
        borderWidth: 1.5,
      },
    ],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[300],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      hideDelay: 0,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof payRatioHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "PayRatioItem") {
          return "";
        }
        const moneyFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: node.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        return renderToStaticMarkup(
          <KeyValueTooltip
            x={this.key}
            keyValues={[
              {
                key: labels.ratio,
                value: `${node.payRatio > 1 ? node.payRatio.toFixed(0) : node.payRatio.toFixed(1)}:1`,
                color: colors.violet[600],
              },
              {
                key: labels.medianEmployeePay,
                value: moneyFormatter.format(node.medianEmployeePay),
              },
              {
                key: labels.ceoPay,
                value: moneyFormatter.format(node.ceoPay),
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
      column: {
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
  };

  return (
    <KioskContext>
      <OnlyPremiumRender>
        <HighchartsChart options={options} className="h-96 xl:h-full" />
      </OnlyPremiumRender>
    </KioskContext>
  );
}
